// src/components/ProfileCompletionModal.js
// Non-dismissible modal that collects the four contact fields and writes them to the
// user's own document. The PARENT decides whether to render this (using isProfileComplete
// + scope checks); this component only owns the form + the save. There is intentionally
// no close button and no backdrop-click-to-close.
//
// Props:
//   userData   : current user doc data (used to prefill any partial values)
//   onComplete : called after a successful save so the parent can re-check and dismiss
import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Crown } from 'lucide-react';
import ProfileContactFields from './ProfileContactFields';
import { validateProfileFields } from '../config/locationData';

function ProfileCompletionModal({ userData = {}, onComplete }) {
  const { user } = useAuth();
  const [values, setValues] = useState({
    phoneNumber: userData.phoneNumber || '',
    country: userData.country || '',
    stateProvince: userData.stateProvince || '',
    city: userData.city || '',
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleChange = (update) => {
    setValues((prev) => ({ ...prev, ...update }));
  };

  const handleSave = async () => {
    setSaveError(null);

    const validationErrors = validateProfileFields(values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    if (!user) {
      setSaveError('You appear to be signed out. Please refresh and try again.');
      return;
    }

    setIsSaving(true);
    try {
      // updateDoc writes ONLY these four fields. It never touches the children array
      // or any other part of the document — this is the data-integrity safeguard.
      await updateDoc(doc(db, 'users', user.uid), {
        phoneNumber: values.phoneNumber.trim(),
        country: values.country.trim(),
        stateProvince: values.stateProvince.trim(),
        city: values.city.trim(),
      });
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Failed to save profile contact info:', err);
      setSaveError('Something went wrong saving your information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-center mb-4">
          <Crown className="text-purple-500" size={28} />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 text-center mb-2">
          Complete Your Contact Information
        </h2>
        <p className="text-gray-600 text-center text-sm mb-6">
          Please add a few details to your account before continuing. This is a one-time step.
        </p>

        <ProfileContactFields values={values} onChange={handleChange} errors={errors} />

        {saveError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-red-700 text-sm">{saveError}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="mt-6 w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:transform-none shadow-lg"
        >
          {isSaving ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Saving...
            </span>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
}

export default ProfileCompletionModal;
